import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { formatTime12 } from '../lib/dateUtils'
import { googleMapsUrl } from '../lib/mapUtils'
import { displayCrewName } from '../lib/crew'

// Shared "job history for one property" modal. Opened from the Properties tab
// and from the calendar's job details modal (so closing returns to the calendar).
export default function PropertyJobsModal({ isOpen, onClose, property }) {
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isOpen || !property) return

    let isMounted = true
    const load = async () => {
      setLoading(true)
      try {
        const { data, error } = await supabase
          .from('jobs')
          .select('*')
          .eq('property_id', property.id)
          // intentionally includes is_deleted jobs so history is a complete audit trail
          .order('job_date', { ascending: false })
          .order('start_time', { ascending: false })
          .limit(100) // Limit to most recent 100 jobs
        if (!error && isMounted) setJobs(data || [])
      } catch (e) {
        console.warn('Could not load jobs:', e.message)
      } finally {
        if (isMounted) setLoading(false)
      }
    }
    load()
    return () => { isMounted = false }
  }, [isOpen, property])

  if (!isOpen || !property) return null

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr + 'T00:00:00')
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
  }

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-yellow-100 text-yellow-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
    }
    return colors[status] || 'bg-gray-100 text-gray-700'
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{property.name}</h2>
              <div className="flex items-center gap-1.5 mt-0.5">
                <p className="text-sm text-gray-500">{property.address}</p>
                {property.address && (
                  <a
                    href={googleMapsUrl(property.address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open in Google Maps"
                    className="p-0.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <svg className="animate-spin w-6 h-6 text-brand-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">No jobs recorded for this property</p>
            </div>
          ) : (
            <div className="space-y-3">
              {jobs.map(job => (
                <div key={job.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900">
                          {formatDate(job.job_date)}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(job.status)}`}>
                          {job.status}
                        </span>
                        {job.is_deleted && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-500 line-through">
                            deleted
                          </span>
                        )}
                        {job.flag === 'urgent' && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
                            Urgent
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-gray-600">
                        {formatTime12(job.start_time)} – {formatTime12(job.end_time)} • {displayCrewName(job.crew_name)}
                      </div>
                      {job.comments && (
                        <p className="mt-1 text-sm text-gray-500 italic">{job.comments}</p>
                      )}
                    </div>
                    {!job.is_deleted && (
                      <a
                        href={`/?date=${job.job_date}&highlightJob=${job.id}`}
                        className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 rounded-lg transition-colors"
                        title="Jump to this job on the calendar"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        View on calendar
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">{jobs.length} job{jobs.length !== 1 ? 's' : ''} total</span>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
